/* eslint-disable no-console */

/**
 * Enhanced Printer Discovery Service
 * Frontend-only solution for discovering and managing printers
 * kware: خدمة محسنة لاكتشاف الطابعات بدون الحاجة لـ Backend
 */
class EnhancedPrinterDiscovery {
  constructor() {
    this.savedPrinters = this.loadSavedPrinters();
    this.connectionCache = new Map();
    this.discoveryHistory = [];
  }

  /**
   * Comprehensive printer discovery
   */
  async discoverPrinters() {
    const results = {
      usb: await this.discoverUSBPrinters(),
      serial: await this.discoverSerialPrinters(),
      manual: this.getManualPrinterOptions(),
      saved: this.getSavedPrinters()
    };

    return this.consolidateResults(results);
  }

  /**
   * Enhanced USB printer discovery
   */
  async discoverUSBPrinters() {
    if (!('usb' in navigator)) {
      return [];
    }

    try {
      // Get previously authorized devices
      const devices = await navigator.usb.getDevices();

      const printers = devices.map(device => ({
        id: `usb-${device.vendorId}-${device.productId}`,
        name: this.getUSBPrinterName(device),
        type: 'usb',
        device,
        status: 'available',
        capabilities: this.inferCapabilities(device.vendorId),
        connectionType: 'USB Direct',
        lastSeen: new Date(),
        trusted: true // Previously authorized
      }));

      // Add option to discover new USB devices
      printers.push({
        id: 'usb-discover-new',
        name: 'البحث عن طابعة USB جديدة',
        type: 'usb-discover',
        status: 'manual',
        action: 'discover',
        connectionType: 'USB Direct',
        description: 'اكتشاف وإضافة طابعة USB جديدة'
      });

      return printers;
    } catch (error) {
      console.error('USB discovery failed:', error);
      return this.getUSBFallbackOptions();
    }
  }

  /**
   * Enhanced Serial printer discovery
   */
  async discoverSerialPrinters() {
    if (!('serial' in navigator)) {
      return [];
    }

    try {
      const ports = await navigator.serial.getPorts();

      const printers = ports.map((port, index) => ({
        id: `serial-${index}`,
        name: `طابعة Serial ${index + 1}`,
        type: 'serial',
        port,
        status: 'available',
        capabilities: ['ZEBRA_ZPL', 'ESC_POS', 'BROTHER_PTOUCH'],
        connectionType: 'USB-Serial',
        lastSeen: new Date(),
        trusted: true,
        baudRate: 9600 // Default
      }));

      // Add option to connect to new serial device
      printers.push({
        id: 'serial-connect-new',
        name: 'الاتصال بطابعة Serial جديدة',
        type: 'serial-discover',
        status: 'manual',
        action: 'connect',
        connectionType: 'USB-Serial',
        description: 'الاتصال بطابعة عبر منفذ Serial جديد'
      });

      return printers;
    } catch (error) {
      console.error('Serial discovery failed:', error);
      return this.getSerialFallbackOptions();
    }
  }

  /**
   * Get manual printer options
   */
  getManualPrinterOptions() {
    return [
      {
        id: 'network-file',
        name: 'طابعة شبكة (تحميل ملف)',
        type: 'network-file',
        description: 'تحميل ملف ZPL/ESC-POS للطباعة عبر الشبكة',
        status: 'manual',
        capabilities: ['ZEBRA_ZPL', 'ESC_POS', 'BROTHER_PTOUCH'],
        connectionType: 'Network File',
        instructions: 'سيتم تحميل ملف للطباعة في نظام منفصل'
      },
      {
        id: 'offline-export',
        name: 'تصدير للطباعة الخارجية',
        type: 'offline',
        description: 'تصدير الملفات للطباعة في نظام منفصل',
        status: 'manual',
        capabilities: ['ZEBRA_ZPL', 'ESC_POS'],
        connectionType: 'Offline Export',
        instructions: 'تصدير ملفات متوافقة مع أنظمة الطباعة الخارجية'
      },
      {
        id: 'browser-print',
        name: 'طباعة المتصفح',
        type: 'browser',
        description: 'طباعة HTML عبر متصفح الويب',
        status: 'manual',
        capabilities: ['HTML', 'PDF'],
        connectionType: 'Browser Print',
        instructions: 'طباعة عادية عبر نافذة المتصفح'
      }
    ];
  }

  /**
   * Get saved printers from localStorage
   */
  getSavedPrinters() {
    return Object.values(this.savedPrinters).map(printer => ({
      ...printer,
      status: 'saved',
      lastUsed: new Date(printer.lastUsed || Date.now())
    }));
  }

  /**
   * Consolidate discovery results
   */
  consolidateResults(results) {
    const allPrinters = [
      ...results.usb,
      ...results.serial,
      ...results.manual,
      ...results.saved
    ];

    // Remove duplicates and sort by priority
    const uniquePrinters = this.removeDuplicates(allPrinters);
    const sortedPrinters = this.sortPrintersByPriority(uniquePrinters);

    return {
      printers: sortedPrinters,
      counts: {
        usb: results.usb.length,
        serial: results.serial.length,
        manual: results.manual.length,
        saved: results.saved.length,
        total: sortedPrinters.length
      },
      capabilities: this.getBrowserCapabilities()
    };
  }

  /**
   * Get USB printer name from device info
   */
  getUSBPrinterName(device) {
    const vendors = {
      0x0A5F: 'Zebra Technologies',
      0x04F9: 'Brother Industries',
      0x04B8: 'Epson',
      0x1504: 'Citizen',
      0x0619: 'Star Micronics',
      0x067B: 'Prolific Technology',
      0x0403: 'FTDI'
    };

    const vendorName = vendors[device.vendorId] || `Unknown Vendor (${device.vendorId.toString(16)})`;
    const productId = device.productId ? ` (${device.productId.toString(16)})` : '';

    return `${vendorName} Printer${productId}`;
  }

  /**
   * Infer printer capabilities from vendor ID
   */
  inferCapabilities(vendorId) {
    const capabilityMap = {
      0x0A5F: ['ZEBRA_ZPL', 'ZEBRA_EPL'], // Zebra
      0x04F9: ['BROTHER_PTOUCH', 'ESC_POS'], // Brother
      0x04B8: ['ESC_POS'], // Epson
      0x1504: ['ESC_POS'], // Citizen
      0x0619: ['ESC_POS'], // Star
      0x067B: ['ZEBRA_ZPL', 'ESC_POS'], // Prolific (USB-Serial adapter)
      0x0403: ['ZEBRA_ZPL', 'ESC_POS'] // FTDI (USB-Serial adapter)
    };

    return capabilityMap[vendorId] || ['ESC_POS', 'ZEBRA_ZPL'];
  }

  /**
   * Get fallback USB options when discovery fails
   */
  getUSBFallbackOptions() {
    return [
      {
        id: 'usb-manual-zebra',
        name: 'طابعة Zebra USB (يدوي)',
        type: 'usb-manual',
        status: 'manual',
        capabilities: ['ZEBRA_ZPL'],
        connectionType: 'USB Direct',
        description: 'اتصال يدوي بطابعة Zebra عبر USB'
      },
      {
        id: 'usb-manual-generic',
        name: 'طابعة USB عامة (يدوي)',
        type: 'usb-manual',
        status: 'manual',
        capabilities: ['ESC_POS'],
        connectionType: 'USB Direct',
        description: 'اتصال يدوي بطابعة عامة عبر USB'
      }
    ];
  }

  /**
   * Get fallback Serial options when discovery fails
   */
  getSerialFallbackOptions() {
    return [
      {
        id: 'serial-manual',
        name: 'طابعة Serial (يدوي)',
        type: 'serial-manual',
        status: 'manual',
        capabilities: ['ZEBRA_ZPL', 'ESC_POS'],
        connectionType: 'USB-Serial',
        description: 'اتصال يدوي بطابعة عبر منفذ Serial'
      }
    ];
  }

  /**
   * Remove duplicate printers
   */
  removeDuplicates(printers) {
    const seen = new Set();
    return printers.filter(printer => {
      const key = `${printer.type}-${printer.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Sort printers by priority
   */
  sortPrintersByPriority(printers) {
    const priorityOrder = {
      'saved': 1,
      'usb': 2,
      'serial': 3,
      'network-file': 4,
      'browser': 5,
      'offline': 6
    };

    return printers.sort((a, b) => {
      const priorityA = priorityOrder[a.type] || 999;
      const priorityB = priorityOrder[b.type] || 999;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Secondary sort by name
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Get browser capabilities
   */
  getBrowserCapabilities() {
    return {
      webUSB: 'usb' in navigator,
      webSerial: 'serial' in navigator,
      webBluetooth: 'bluetooth' in navigator,
      serviceWorker: 'serviceWorker' in navigator,
      fileAPI: 'FileReader' in window
    };
  }

  /**
   * Save printer to localStorage
   */
  savePrinter(printer) {
    this.savedPrinters[printer.id] = {
      ...printer,
      saved: true,
      lastUsed: new Date().toISOString()
    };

    try {
      localStorage.setItem('folio-printers', JSON.stringify(this.savedPrinters));
    } catch (error) {
      console.error('Failed to save printer:', error);
    }
  }

  /**
   * Load saved printers from localStorage
   */
  loadSavedPrinters() {
    try {
      const saved = localStorage.getItem('folio-printers');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Failed to load saved printers:', error);
      return {};
    }
  }

  /**
   * Remove saved printer
   */
  removeSavedPrinter(printerId) {
    delete this.savedPrinters[printerId];

    try {
      localStorage.setItem('folio-printers', JSON.stringify(this.savedPrinters));
    } catch (error) {
      console.error('Failed to remove saved printer:', error);
    }
  }

  /**
   * Get printer by ID
   */
  getPrinter(printerId) {
    return this.savedPrinters[printerId] || null;
  }

  /**
   * Update printer last used time
   */
  updatePrinterUsage(printerId) {
    if (this.savedPrinters[printerId]) {
      this.savedPrinters[printerId].lastUsed = new Date().toISOString();
      this.savePrinter(this.savedPrinters[printerId]);
    }
  }
}

export default EnhancedPrinterDiscovery;
